import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Receipt, Upload, Trash, PencilSimple, X, Image as ImageIcon, FileCsv } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";
import ExpenseScanDialog from "../components/ExpenseScanDialog";
import ExpenseImports from "../components/ExpenseImports";
import { useAuth } from "../AuthContext";

const CATEGORIES = ["Shipping", "Supplies", "Packaging", "Marketing", "Travel", "Dispensers", "Base Ingredients", "Lab Equipment", "Other"];

export default function ExpensesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScan, setShowScan] = useState(false);
  const [editing, setEditing] = useState(null);  // expense being edited
  const [previewing, setPreviewing] = useState(null); // expense being viewed
  const [filterCat, setFilterCat] = useState("");
  const [importsKey, setImportsKey] = useState(0);  // bumped after a new import + after row deletes
  const isElevated = !!user && ["owner", "admin", "cfo"].includes(user.role);

  const load = async () => {
    try {
      const { data } = await api.get("/expenses", { params: filterCat ? { category: filterCat } : {} });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterCat]);

  const exportCsv = async () => {
    try {
      const url = `${(api.defaults.baseURL || "")}/expenses/export.csv${filterCat ? `?category=${encodeURIComponent(filterCat)}` : ""}`;
      const res = await api.get(`/expenses/export.csv${filterCat ? `?category=${encodeURIComponent(filterCat)}` : ""}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "expenses.csv";
      link.click();
      URL.revokeObjectURL(link.href);
      void url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm(t("expenses.confirm_delete"))) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success(t("expenses.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalShown = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("expenses.title")}</h1>
          <p className="text-sm text-zinc-600 mt-1">
            {isElevated ? t("expenses.subtitle_owner") : t("expenses.subtitle_dist")}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <BrutalButton variant="secondary" onClick={exportCsv} data-testid="export-csv-btn">
            <FileCsv size={14} weight="bold" /> {t("expenses.export_csv")}
          </BrutalButton>
          <BrutalButton onClick={() => setShowScan(true)} data-testid="open-scan-expense-btn">
            <Upload size={14} weight="bold" /> {t("expenses.add_btn")}
          </BrutalButton>
        </div>
      </div>

      <ExpenseImports
        refreshKey={importsKey}
        onAfterUndo={() => { setImportsKey((k) => k + 1); load(); }}
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-zinc-500">{t("expenses.filter_label")}:</span>
        <button
          type="button"
          onClick={() => setFilterCat("")}
          className={`text-xs px-2.5 h-7 rounded-full border ${!filterCat ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
          data-testid="filter-cat-all"
        >
          {t("expenses.filter_all")}
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setFilterCat(c)}
            className={`text-xs px-2.5 h-7 rounded-full border ${filterCat === c ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
            data-testid={`filter-cat-${c.toLowerCase()}`}
          >
            {t(`expenses.cat_${c.toLowerCase().replace(/ /g, "_")}`)}
          </button>
        ))}
        <span className="ml-auto text-xs text-zinc-600">
          {t("expenses.total_shown")}: <span className="font-semibold text-zinc-900 tabular-nums">${totalShown.toFixed(2)}</span>
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500">{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <BrutalCard>
          <div className="flex items-start gap-3 text-zinc-600">
            <Receipt size={22} weight="bold" className="shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed">{t("expenses.empty")}</p>
          </div>
        </BrutalCard>
      ) : (
        <div className="space-y-2" data-testid="expense-rows">
          {rows.map((r) => (
            <ExpenseRow
              key={r.id}
              row={r}
              isElevated={isElevated}
              currentUserId={user?.id}
              onPreview={() => setPreviewing(r)}
              onEdit={() => setEditing(r)}
              onDelete={() => removeRow(r.id)}
              fmtDate={(iso) => new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US", { month: "short", day: "numeric", year: "numeric" })}
              t={t}
            />
          ))}
        </div>
      )}

      {showScan && (
        <ExpenseScanDialog
          onClose={() => setShowScan(false)}
          onSaved={() => { setShowScan(false); load(); }}
        />
      )}
      {editing && (
        <ExpenseScanDialog
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {previewing && (
        <ReceiptPreviewDialog expense={previewing} onClose={() => setPreviewing(null)} t={t} />
      )}
    </div>
  );
}

function ExpenseRow({ row, isElevated, currentUserId, onPreview, onEdit, onDelete, fmtDate, t }) {
  const canEdit = isElevated || row.user_id === currentUserId;
  const catKey = `expenses.cat_${(row.category || "Other").toLowerCase().replace(/ /g, "_")}`;
  return (
    <div
      className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm flex items-center gap-3"
      data-testid={`expense-row-${row.id}`}
    >
      <button
        type="button"
        onClick={onPreview}
        className="w-12 h-12 rounded-lg border border-zinc-200 bg-zinc-50 flex items-center justify-center shrink-0 hover:bg-zinc-100"
        title={t("expenses.view_receipt")}
        data-testid={`receipt-preview-${row.id}`}
      >
        {row.receipt_image_url ? (
          <ImageIcon size={18} weight="bold" className="text-zinc-600" />
        ) : (
          <Receipt size={18} weight="bold" className="text-zinc-400" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-zinc-900 truncate">{row.vendor || t("expenses.no_vendor")}</span>
          <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-full">
            {t(catKey, row.category)}
          </span>
        </div>
        <div className="text-xs text-zinc-500 mt-0.5 truncate">
          {fmtDate(row.date)} · {row.user_name || row.user_email}
          {row.notes && <span className="ml-2 italic">&ldquo;{row.notes}&rdquo;</span>}
        </div>
      </div>
      <span className="text-base font-semibold text-zinc-900 tabular-nums shrink-0" data-testid={`expense-amount-${row.id}`}>
        ${Number(row.amount).toFixed(2)}
      </span>
      {canEdit && (
        <div className="flex gap-1 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="w-8 h-8 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 flex items-center justify-center text-zinc-600"
            data-testid={`edit-expense-${row.id}`}
            title={t("common.edit")}
          >
            <PencilSimple size={14} weight="bold" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="w-8 h-8 rounded-md border border-rose-200 bg-white hover:bg-rose-50 flex items-center justify-center text-rose-600"
            data-testid={`delete-expense-${row.id}`}
            title={t("common.delete")}
          >
            <Trash size={14} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}

function ReceiptPreviewDialog({ expense, onClose, t }) {
  const fileUrl = expense.receipt_image_url
    ? `${(api.defaults.baseURL || "")}/files/${expense.receipt_image_url}`
    : null;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="receipt-preview-dialog"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold">{expense.vendor || t("expenses.no_vendor")}</h2>
            <p className="text-xs text-zinc-500">{(expense.date || "").slice(0, 10)} · {expense.category}</p>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="receipt-preview-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="mt-2 text-2xl font-bold text-zinc-900 tabular-nums">${Number(expense.amount).toFixed(2)}</div>
        {expense.notes && <p className="text-sm text-zinc-700 mt-2 italic">&ldquo;{expense.notes}&rdquo;</p>}
        {fileUrl ? (
          <img
            src={fileUrl}
            alt="receipt"
            className="mt-4 max-h-[60vh] w-full object-contain rounded-lg border border-zinc-200 bg-zinc-50"
            data-testid="receipt-image"
          />
        ) : (
          <div className="mt-4 text-sm text-zinc-500 italic text-center py-8 border border-dashed border-zinc-200 rounded-lg">
            {t("expenses.no_receipt_image")}
          </div>
        )}
      </div>
    </div>
  );
}
